// src/components/admin/scheduling/student_calendar/StudentCalendar.js

import "./StudentCalendar.css";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { flushSync } from "react-dom";
import { useLocation, useNavigate } from "react-router-dom";
import { Check, ChevronDown, X } from "lucide-react";
import { SessionEditor } from "../components/EditSessionModal";
import SessionNoteTooltip from "../components/SessionNoteTooltip";
import StudentCreateSessionModal from "./StudentCreateSessionModal";
import useAdminScheduleHotkeys from "../useAdminScheduleHotkeys";
import { loadClipboard, saveClipboard } from "../clipboardStore";
import {
  fetchAllStudents,
  fetchActiveTutors,
  updateScheduledSession,
  deleteScheduledSession,
  createScheduledSession,
  fetchAvailabilityBlocks,
  fetchScheduledSessions,
  fetchDayConfigsForDates,
  subscribeToStudentSessionsInRange,
} from "../schedulingApi";
import { useDeveloperModeFlag } from "../developerModeTools";
import {
  addMonths,
  monthGridDays,
  sameYMD,
  startOfMonth,
  ymd,
  isDisabledDay,
} from "../../employee/availability/availabilityUtils";
import {
  // Shared calendar-grid components (also used by ModalCalendarPanel)
  MonthSection,
  monthKey,
  toDate,
  isPastDateValue,
} from "./StudentCalendarGrid";

// ─── Constants ────────────────────────────────────────────────────────────────

const PAST_INIT   = 2;
const FUTURE_INIT = 4;
const LOAD_PAST   = 3;
const LOAD_FUTURE = 3;

// Wide subscription range so real-time updates cover all visible months
const SUB_PAST_MONTHS   = 18;
const SUB_FUTURE_MONTHS = 18;

function buildCreateDraftForDate(date, student) {
  if (!date || !student) return null;
  const start = new Date(date);
  start.setHours(14, 0, 0, 0);
  const end = new Date(date);
  end.setHours(15, 50, 0, 0);
  return {
    studentId: student.id,
    studentName: student.name,
    studentGrade: student.grade,
    status: "Pending",
    subject: "",
    note: "",
    tutorUid: "",
    tutorName: "",
    dayKey: date.toLocaleDateString("en-US", { weekday: "long" }).toLowerCase(),
    blockId: "",
    slotIdx: 0,
    start,
    end,
  };
}

function captureAnchor(el) {
  if (!el) return null;
  const cRect = el.getBoundingClientRect();
  const nodes = el.querySelectorAll("[data-month-key]");
  for (const node of nodes) {
    const r = node.getBoundingClientRect();
    if (r.bottom > cRect.top) {
      return { key: node.getAttribute("data-month-key"), top: r.top - cRect.top };
    }
  }
  return null;
}

function isWheelIsolatedTarget(target) {
  return !!(
    target &&
    typeof target.closest === "function" &&
    target.closest("[data-wheel-isolated='true']")
  );
}

// ─── StudentSearchSelect ──────────────────────────────────────────────────────

function StudentSearchSelect({ students, value, onChange, locked, lockedName }) {
  const [open,   setOpen]   = useState(false);
  const [search, setSearch] = useState("");
  const rootRef  = useRef(null);
  const inputRef = useRef(null);
  const [hoveredId, setHoveredId] = useState(null);

  const selected = students.find(s => s.id === value);

  const filtered = useMemo(() => {
    if (!search) return students;
    const q = search.toLowerCase();
    return students.filter(s => s.name.toLowerCase().includes(q));
  }, [students, search]);

  useEffect(() => {
    if (open) { setSearch(""); setTimeout(() => inputRef.current?.focus(), 0); }
  }, [open]);

  useEffect(() => {
    const onDocDown = (e) => { if (!rootRef.current?.contains(e.target)) setOpen(false); };
    const onEsc    = (e) => { if (e.key === "Escape") setOpen(false); };
    document.addEventListener("mousedown", onDocDown);
    document.addEventListener("keydown", onEsc);
    return () => {
      document.removeEventListener("mousedown", onDocDown);
      document.removeEventListener("keydown", onEsc);
    };
  }, []);

  if (locked) {
    return (
      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
        <span style={{ fontSize: 12, fontWeight: 500, color: "#9ca3af", flexShrink: 0 }}>
          Viewing Schedule For:
        </span>
        <span style={{ fontSize: 13, fontWeight: 600, color: "#111827" }}>{lockedName}</span>
      </div>
    );
  }

  return (
    <div ref={rootRef} style={{ position: "relative", flexShrink: 0 }}>
      <button
        type="button"
        onClick={() => setOpen(p => !p)}
        style={{
          height: 32,
          padding: "0 11px",
          borderRadius: 9,
          border: open ? "1px solid #9ca3af" : "1px solid #e5e7eb",
          fontSize: 13,
          color: selected ? "#111827" : "#9ca3af",
          background: "#fff",
          outline: "none",
          cursor: "pointer",
          display: "flex",
          alignItems: "center",
          gap: 6,
          minWidth: 200,
          boxShadow: open ? "0 0 0 3px rgba(17,24,39,0.06)" : "0 1px 1px rgba(0,0,0,0.02)",
          transition: "border-color 0.15s, box-shadow 0.15s",
        }}
      >
        <span style={{ flex: 1, textAlign: "left", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
          {selected?.name || "Select student…"}
        </span>
        {value && (
          <span
            role="button"
            tabIndex={0}
            onClick={(e) => { e.stopPropagation(); onChange(null); }}
            onKeyDown={(e) => { if (e.key === "Enter") { e.stopPropagation(); onChange(null); } }}
            style={{ display: "flex", alignItems: "center", color: "#9ca3af", flexShrink: 0, padding: "0 2px" }}
            title="Clear"
          >
            <X size={12} />
          </span>
        )}
        <ChevronDown
          size={14}
          color="#6b7280"
          style={{ flexShrink: 0, transform: open ? "rotate(180deg)" : "none", transition: "transform 0.15s" }}
        />
      </button>

      {open && (
        <div data-wheel-isolated="true" style={{
          position: "absolute",
          top: "calc(100% + 6px)",
          right: 0,
          minWidth: 240,
          background: "#fff",
          border: "1px solid #e5e7eb",
          borderRadius: 10,
          boxShadow: "0 8px 24px rgba(15,23,42,0.10)",
          padding: 6,
          zIndex: 60,
          maxHeight: 320,
          overflow: "hidden",
          display: "flex",
          flexDirection: "column",
        }}>
          <input
            ref={inputRef}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search student…"
            style={{
              border: "1px solid #e5e7eb",
              borderRadius: 7,
              padding: "6px 10px",
              fontSize: 13,
              outline: "none",
              marginBottom: 4,
              flexShrink: 0,
              color: "#111827",
            }}
          />
          <div style={{ overflowY: "auto", flex: 1 }}>
            {filtered.length === 0 ? (
              <div style={{ padding: "8px 9px", fontSize: 13, color: "#9ca3af" }}>No results</div>
            ) : (
              filtered.map(s => {
                const isSel     = s.id === value;
                const isHovered = s.id === hoveredId;
                return (
                  <button
                    key={s.id}
                    type="button"
                    onMouseEnter={() => setHoveredId(s.id)}
                    onMouseLeave={() => setHoveredId(null)}
                    onClick={() => { onChange(s); setOpen(false); }}
                    style={{
                      width: "100%",
                      border: "none",
                      background: isSel
                        ? "#e9eaec"
                        : isHovered
                          ? "#f3f4f6"
                          : "transparent",
                      borderRadius: 7,
                      padding: "8px 9px",
                      fontSize: 13,
                      fontWeight: isSel ? 600 : 400,
                      textAlign: "left",
                      cursor: "pointer",
                      color: "#111827",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      transition: "background 0.1s",
                    }}
                  >
                    <span>{s.name}</span>
                    {isSel && <Check size={13} color="#374151" />}
                  </button>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// ─── SessionModal ─────────────────────────────────────────────────────────────
// Clean centered modal wrapping SessionEditor logic

function SessionModal({ activePanel, students, tutors, sessions, onSave, onCreate, onDelete, onClose, isPastLocked, forceReadOnly = false }) {
  if (!activePanel) return null;

  const isCreate  = activePanel.kind === "create";
  const session   = activePanel.data;
  const isReadOnly = forceReadOnly || (!isCreate && isPastLocked(session?.start));

  const dateStr = session?.start
    ? new Date(session.start).toLocaleDateString("en-US", {
        weekday: "long", month: "long", day: "numeric", year: "numeric",
      })
    : "";

  return (
    <div
      className="sc-modal-overlay"
      style={{
        position: "fixed", inset: 0,
        background: "rgba(15,23,42,0.30)",
        display: "flex", alignItems: "center", justifyContent: "center",
        zIndex: 9999, padding: 20,
      }}
      onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div
        className="sc-modal-card"
        style={{
          width: "min(560px, 100%)",
          maxHeight: "90vh",
          background: "#fff",
          borderRadius: 14,
          border: "1px solid #e5e7eb",
          boxShadow: "0 20px 60px rgba(15,23,42,0.18)",
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
        }}
        onMouseDown={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div style={{
          padding: "16px 20px 14px",
          borderBottom: "1px solid #f3f4f6",
          display: "flex",
          alignItems: "flex-start",
          justifyContent: "space-between",
          flexShrink: 0,
        }}>
          <div>
            <div style={{ fontSize: 16, fontWeight: 700, color: "#111827", letterSpacing: "-0.2px" }}>
              {isCreate ? "Create Session" : (isReadOnly ? "View Session" : "Edit Session")}
            </div>
            {dateStr && (
              <div style={{ fontSize: 12, color: "#9ca3af", marginTop: 2 }}>{dateStr}</div>
            )}
          </div>
          <button
            onClick={onClose}
            style={{
              border: "1px solid #e5e7eb", borderRadius: 8,
              width: 28, height: 28, background: "#fff", color: "#6b7280",
              fontSize: 16, cursor: "pointer", display: "flex",
              alignItems: "center", justifyContent: "center", flexShrink: 0,
            }}
          >
            ×
          </button>
        </div>

        {/* Body — SessionEditor handles all form logic + action buttons */}
        <div style={{ overflowY: "auto", flex: 1, padding: "16px 20px" }}>
          <SessionEditor
            initialSession={session}
            students={students}
            tutors={tutors}
            allWeekSessions={sessions}
            onCreate={isCreate ? onCreate : null}
            onSave={onSave}
            onDelete={onDelete}
            onCancel={onClose}
            isReadOnly={isReadOnly}
          />
        </div>
      </div>
    </div>
  );
}

// ─── DayCell and MonthSection are imported from StudentCalendarGrid.js ────────
// They are also used by ModalCalendarPanel.js for the session modal calendar.

// ─── Main component ───────────────────────────────────────────────────────────

export default function StudentCalendar({ lockedStudent = null, embedded = false, readOnly = false }) {
  const location = useLocation();
  const navigate = useNavigate();
  const { isDeveloperMode } = useDeveloperModeFlag();
  const isPastLocked = (value) => !isDeveloperMode && isPastDateValue(value);
  const routeStudentId = useMemo(() => new URLSearchParams(location.search).get("student") || "", [location.search]);

  const BASE  = useMemo(() => startOfMonth(new Date()), []);
  const today = useMemo(() => new Date(), []);

  // ── Multi-month state ──
  const [months,    setMonths]    = useState(() =>
    Array.from({ length: PAST_INIT + 1 + FUTURE_INIT }, (_, i) => addMonths(BASE, i - PAST_INIT))
  );
  const [pastCount, setPastCount] = useState(PAST_INIT);

  // ── Data state ──
  const [students,        setStudents]        = useState([]);
  const [selectedStudent, setSelectedStudent] = useState(lockedStudent || null);
  const [sessions,        setSessions]        = useState([]);
  const [dayConfigsMap,   setDayConfigsMap]   = useState({});
  const [tutors,          setTutors]          = useState([]);
  const [isLoadingMore,   setIsLoadingMore]   = useState(false);

  // ── Interaction state ──
  const [selectedSessionId, setSelectedSessionId] = useState(null);
  const [selectedDate,      setSelectedDate]      = useState(null);
  // eslint-disable-next-line no-unused-vars
  const [createDraft,       setCreateDraft]       = useState(null);
  const [createDate,        setCreateDate]        = useState(null);
  const [createInitialData, setCreateInitialData] = useState(null);
  const [hoveredNote,       setHoveredNote]       = useState(null);
  const [tooltipAnchor,     setTooltipAnchor]     = useState(null);
  const [activePanel,       setActivePanel]       = useState(null); // { kind, data }

  // ── Refs ──
  const containerRef      = useRef(null);
  const currentMonthRef   = useRef(null);
  const isLoadingPastRef  = useRef(false);
  const isLoadingFutRef   = useRef(false);
  const loadedStartRef    = useRef(-PAST_INIT);
  const loadedEndRef      = useRef(FUTURE_INIT);
  const epochRef          = useRef(0);
  const subscriptionRef   = useRef(null);   // Firestore unsubscribe fn

  // ── Subscription: real-time session updates ──
  const setupSubscription = useCallback((studentId) => {
    // Tear down existing listener
    subscriptionRef.current?.();
    subscriptionRef.current = null;
    setSessions([]);

    if (!studentId) return;

    const subStart = addMonths(BASE, -SUB_PAST_MONTHS);
    const subEnd   = addMonths(BASE,  SUB_FUTURE_MONTHS + 1);

    subscriptionRef.current = subscribeToStudentSessionsInRange(
      studentId, subStart, subEnd,
      (rows) => setSessions(rows)
    );
  }, [BASE]);

  // Cleanup on unmount
  useEffect(() => {
    return () => { subscriptionRef.current?.(); };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Bootstrap: reset calendar when student changes ──
  useEffect(() => {
    epochRef.current += 1;
    const epoch = epochRef.current;

    const initMonths = Array.from(
      { length: PAST_INIT + 1 + FUTURE_INIT },
      (_, i) => addMonths(BASE, i - PAST_INIT)
    );
    setMonths(initMonths);
    setPastCount(PAST_INIT);
    loadedStartRef.current = -PAST_INIT;
    loadedEndRef.current   = FUTURE_INIT;
    setDayConfigsMap({});
    setSelectedDate(null);
    setSelectedSessionId(null);
    setCreateDraft(null);
    setActivePanel(null);

    setTimeout(() => {
      currentMonthRef.current?.scrollIntoView({ block: "start", behavior: "instant" });
    }, 80);

    // Set up real-time session listener for this student
    setupSubscription(selectedStudent?.id);

    // Load day configs for initial range
    (async () => {
      try {
        const rangeMonths = Array.from(
          { length: PAST_INIT + 1 + FUTURE_INIT },
          (_, i) => addMonths(BASE, i - PAST_INIT)
        );
        const allDays = rangeMonths.flatMap(m => monthGridDays(m));
        const cfgs    = await fetchDayConfigsForDates(allDays);
        if (epochRef.current !== epoch) return;
        setDayConfigsMap(cfgs);
      } catch (e) {
        console.error(e);
      }
    })();
  }, [selectedStudent, setupSubscription]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Load students & tutors once ──
  useEffect(() => {
    Promise.all([fetchAllStudents(), fetchActiveTutors()])
      .then(([sData, tData]) => {
        setStudents(sData);
        setTutors(tData);
        if (lockedStudent?.id) {
          const matched = sData.find(s => s.id === lockedStudent.id);
          setSelectedStudent(matched || lockedStudent);
        }
      })
      .catch(console.error);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (lockedStudent?.id) return;

    if (!routeStudentId) {
      if (selectedStudent != null) {
        setSelectedStudent(null);
      }
      return;
    }

    const matchedStudent = students.find((student) => student.id === routeStudentId) || null;
    if (matchedStudent && selectedStudent?.id !== matchedStudent.id) {
      setSelectedStudent(matchedStudent);
    } else if (!matchedStudent && selectedStudent?.id === routeStudentId) {
      setSelectedStudent(null);
    }
  }, [lockedStudent, routeStudentId, selectedStudent, students]);

  // Sync lockedStudent prop updates
  useEffect(() => {
    if (!lockedStudent?.id) return;
    setSelectedStudent(prev =>
      prev?.id === lockedStudent.id ? { ...prev, ...lockedStudent } : lockedStudent
    );
  }, [lockedStudent]);

  // ── Infinite scroll: load more configs when scrolling ──
  const loadMoreFuture = useCallback(async () => {
    if (isLoadingFutRef.current) return;
    isLoadingFutRef.current = true;
    setIsLoadingMore(true);
    const prevEnd = loadedEndRef.current;
    const newEnd  = prevEnd + LOAD_FUTURE;
    const newMons = Array.from({ length: LOAD_FUTURE }, (_, i) => addMonths(BASE, prevEnd + 1 + i));
    try {
      const allDays = newMons.flatMap(m => monthGridDays(m));
      const cfgs    = await fetchDayConfigsForDates(allDays);
      setDayConfigsMap(p => ({ ...p, ...cfgs }));
      setMonths(p => [...p, ...newMons]);
      loadedEndRef.current = newEnd;
    } catch (e) {
      console.error(e);
    } finally {
      isLoadingFutRef.current = false;
      setIsLoadingMore(false);
    }
  }, [BASE]);

  const loadMorePast = useCallback(async () => {
    if (isLoadingPastRef.current) return;
    isLoadingPastRef.current = true;
    const prevStart = loadedStartRef.current;
    const newStart  = prevStart - LOAD_PAST;
    const newMons   = Array.from({ length: LOAD_PAST }, (_, i) => addMonths(BASE, newStart + i));
    try {
      const allDays = newMons.flatMap(m => monthGridDays(m));
      const cfgs    = await fetchDayConfigsForDates(allDays);
      const el      = containerRef.current; // null in embedded mode — that's fine
      const anchor  = el ? captureAnchor(el) : null;
      flushSync(() => {
        setDayConfigsMap(p => ({ ...p, ...cfgs }));
        setMonths(p => [...newMons, ...p]);
        setPastCount(p => p + LOAD_PAST);
        loadedStartRef.current = newStart;
      });
      if (el && anchor?.key) {
        requestAnimationFrame(() => {
          const cRect      = el.getBoundingClientRect();
          const anchorNode = el.querySelector(`[data-month-key="${anchor.key}"]`);
          if (!anchorNode) return;
          el.scrollTop += anchorNode.getBoundingClientRect().top - cRect.top - anchor.top;
        });
      }
    } catch (e) {
      console.error(e);
    } finally {
      isLoadingPastRef.current = false;
    }
  }, [BASE]);

  // ── Infinite scroll via IntersectionObserver (works in both standalone and embedded) ──
  const topSentinelRef    = useRef(null);
  const bottomSentinelRef = useRef(null);

  useEffect(() => {
    const obs = new IntersectionObserver(
      (entries) => {
        for (const e of entries) {
          if (!e.isIntersecting) continue;
          if (e.target === topSentinelRef.current)    loadMorePast();
          if (e.target === bottomSentinelRef.current) loadMoreFuture();
        }
      },
      { rootMargin: "400px" }
    );
    if (topSentinelRef.current)    obs.observe(topSentinelRef.current);
    if (bottomSentinelRef.current) obs.observe(bottomSentinelRef.current);
    return () => obs.disconnect();
  }, [loadMorePast, loadMoreFuture]);

  // ── Sessions by day map ──
  const sessionsByDay = useMemo(() => {
    const map = new Map();
    for (const sess of sessions) {
      const s = toDate(sess.start);
      if (!s || isNaN(s.getTime())) continue;
      const k = ymd(s);
      if (!map.has(k)) map.set(k, []);
      map.get(k).push(sess);
    }
    for (const [k, arr] of map) {
      arr.sort((a, b) => toDate(a.start) - toDate(b.start));
      map.set(k, arr);
    }
    return map;
  }, [sessions]);

  // ── Session handlers ──
  const handleDayClick = (date, isClosed) => {
    if (isClosed || !selectedStudent) return;
    // View-only (e.g. Tutor portal): never open the create panel on empty days.
    if (readOnly) return;
    const isAlreadySel = selectedDate && sameYMD(selectedDate, date);
    const nextDate     = isAlreadySel ? null : date;
    setSelectedDate(nextDate);
    setSelectedSessionId(null);
    if (nextDate && !isPastLocked(nextDate)) {
      const draft = buildCreateDraftForDate(nextDate, selectedStudent);
      setCreateDraft(draft);
      setActivePanel({ kind: "create", data: draft });
    } else {
      setCreateDraft(null);
      setActivePanel(null);
    }
  };

  const handleSessionClick = (sessId, isAlreadySelected) => {
    // View-only (e.g. Tutor portal): cards are display-only — never open a panel.
    if (readOnly) return;
    if (isAlreadySelected) {
      setSelectedSessionId(null);
      setSelectedDate(null);
      setCreateDraft(null);
      setActivePanel(null);
    } else {
      const sess = sessions.find(s => s.id === sessId);
      setSelectedSessionId(sessId);
      setSelectedDate(null);
      setCreateDraft(null);
      setActivePanel(sess ? { kind: "edit", data: sess } : null);
    }
  };

  const handleSelectStudent = (s) => {
    if (lockedStudent?.id) return;
    const nextStudentId = s?.id || "";
    if (selectedStudent?.id !== nextStudentId) {
      setSelectedStudent(s || null);
    }
    setSelectedSessionId(null);
    setSelectedDate(null);
    setCreateDraft(null);
    setActivePanel(null);
    const params = new URLSearchParams(location.search);
    if (nextStudentId) params.set("student", nextStudentId);
    else params.delete("student");
    const nextSearch = params.toString();
    const nextUrl = nextSearch ? `/students/calendar?${nextSearch}` : "/students/calendar";
    if (`${location.pathname}${location.search}` !== nextUrl) {
      navigate(nextUrl);
    }
  };

  const handleCloseModal = () => {
    setActivePanel(null);
    setSelectedSessionId(null);
    setSelectedDate(null);
    setCreateDraft(null);
  };

  const handleSaveSession = async (sessionId, patch) => {
    const source     = sessions.find(s => s.id === sessionId);
    const targetDate = patch?.start ? new Date(patch.start) : source?.start;
    if (isPastLocked(targetDate)) { alert("Past sessions are view-only."); return; }
    try {
      await updateScheduledSession(sessionId, patch);
      // onSnapshot listener auto-updates sessions
      handleCloseModal();
    } catch (err) {
      console.error(err);
      alert("Failed to save changes.");
    }
  };

  const handleDeleteSession = async (sessionId) => {
    const source = sessions.find(s => s.id === sessionId);
    if (isPastLocked(source?.start)) { alert("Past sessions are view-only."); return; }
    try {
      await deleteScheduledSession(sessionId);
      // onSnapshot listener auto-updates sessions
      handleCloseModal();
    } catch (err) {
      console.error(err);
      alert("Failed to delete session.");
    }
  };

  const handleCreateSession = async (payload) => {
    if (isPastLocked(payload?.start)) { alert("Past sessions are view-only."); return; }
    try {
      await createScheduledSession(payload);
      // onSnapshot listener auto-updates sessions
      handleCloseModal();
      setCreateDate(null);
      setCreateInitialData(null);
    } catch (err) {
      console.error(err);
      alert("Failed to create session.");
    }
  };

  // ── Paste availability check ──
  const checkPasteAvailability = async (tutorUid, targetDate, startMin, endMin) => {
    const startOfDay = new Date(targetDate); startOfDay.setHours(0, 0, 0, 0);
    const endOfDay   = new Date(targetDate); endOfDay.setHours(23, 59, 59, 999);
    const [availBlocks, existingSessions] = await Promise.all([
      fetchAvailabilityBlocks({ rangeStart: startOfDay, rangeEnd: endOfDay, tutorUid }),
      fetchScheduledSessions({ rangeStart: startOfDay, rangeEnd: endOfDay }),
    ]);
    const targetStart = new Date(targetDate);
    targetStart.setHours(0, startMin, 0, 0);
    const isCovered = availBlocks.some(b => {
      if (b.unavailable) return false;
      const bS = new Date(b.start), bE = new Date(b.end);
      return bS <= targetStart && bE >= new Date(targetStart.getTime() + (endMin - startMin) * 60000);
    });
    if (!isCovered) return false;
    const targetEnd = new Date(targetStart.getTime() + (endMin - startMin) * 60000);
    const events = [];
    existingSessions.forEach(session => {
      if (session.tutorUid !== tutorUid) return;
      if (String(session.status || "").toLowerCase().includes("cancel")) return;
      const sS = new Date(session.start), sE = new Date(session.end);
      if (!(sS < targetEnd && sE > targetStart)) return;
      const cs = Math.max(sS.getTime(), targetStart.getTime());
      const ce = Math.min(sE.getTime(), targetEnd.getTime());
      if (ce <= cs) return;
      events.push({ ts: cs, delta: 1 }, { ts: ce, delta: -1 });
    });
    events.sort((a, b) => a.ts !== b.ts ? a.ts - b.ts : a.delta - b.delta);
    let active = 0, peak = 0;
    events.forEach(ev => { active += ev.delta; if (active > peak) peak = active; });
    return peak < 3;
  };

  // ── Clipboard ──
  const handleCopy = () => {
    if (!selectedSessionId) return;
    const sess = sessions.find(s => s.id === selectedSessionId);
    if (!sess) return;
    saveClipboard({
      type: "student_calendar_clip",
      payload: {
        subject:  sess.subject,
        note:     sess.note,
        tutorUid: sess.tutorUid,
        duration: (sess.end - sess.start) / 60000,
        startMin: sess.start.getHours() * 60 + sess.start.getMinutes(),
      },
    });
  };

  const handlePaste = async () => {
    if (!selectedDate || !selectedStudent) return;
    if (isDisabledDay(selectedDate, dayConfigsMap)) return;
    if (isPastLocked(selectedDate)) { alert("Past sessions are view-only."); return; }
    const clip = loadClipboard();
    if (!clip || clip.type !== "student_calendar_clip") return;
    const { payload } = clip;
    const startMin = payload.startMin, endMin = startMin + payload.duration;
    const targetStart = new Date(selectedDate);
    targetStart.setHours(Math.floor(startMin / 60), startMin % 60, 0, 0);
    const targetEnd = new Date(selectedDate);
    targetEnd.setHours(Math.floor(endMin / 60), endMin % 60, 0, 0);
    const isAvailable = await checkPasteAvailability(payload.tutorUid, selectedDate, startMin, endMin);
    if (isAvailable) {
      const tutorObj = tutors.find(t => t.id === payload.tutorUid);
      await handleCreateSession({
        studentId: selectedStudent.id, studentName: selectedStudent.name,
        studentGrade: selectedStudent.grade, status: "Pending",
        subject: payload.subject, note: payload.note,
        tutorUid: payload.tutorUid, tutorName: tutorObj?.name || "Unknown",
        dayKey: selectedDate.toLocaleDateString("en-US", { weekday: "long" }).toLowerCase(),
        start: targetStart, end: targetEnd,
      });
    } else {
      setCreateInitialData({ ...payload, endMin });
      setCreateDate(selectedDate);
    }
  };

  const handleDeleteKey = async () => {
    if (!selectedSessionId) return;
    const sel = sessions.find(s => s.id === selectedSessionId);
    if (isPastLocked(sel?.start)) { alert("Past sessions are view-only."); return; }
    if (window.confirm("Delete this session?")) {
      await handleDeleteSession(selectedSessionId);
    }
  };

  useAdminScheduleHotkeys({
    // Disable hotkeys when a modal is open, and entirely in view-only mode
    // (Tutor portal) so copy/paste/delete can never mutate sessions.
    enabled: !activePanel && !readOnly,
    onCopy: handleCopy,
    onPaste: handlePaste,
    onDelete: handleDeleteKey,
    onClearSelection: () => {
      setSelectedSessionId(null);
      setSelectedDate(null);
      setCreateDraft(null);
      setActivePanel(null);
    },
  });

  const scrollToToday = () =>
    currentMonthRef.current?.scrollIntoView({ block: "start", behavior: "smooth" });

  const handleOuterWheel = useCallback((e) => {
    const container = containerRef.current;
    if (!container) return;
    if (isWheelIsolatedTarget(e.target)) return;
    if (container.contains(e.target)) return;
    if (Math.abs(e.deltaY) < 1) return;

    container.scrollTop += e.deltaY;
    e.preventDefault();
  }, []);

  const cellProps = {
    today, sessionsByDay, selectedDate, selectedSessionId, dayConfigsMap, isDeveloperMode,
    onDayClick:     handleDayClick,
    onSessionClick: handleSessionClick,
    onNoteEnter:    (note, rect) => { setHoveredNote(note); setTooltipAnchor(rect); },
    onNoteLeave:    () => { setHoveredNote(null); setTooltipAnchor(null); },
  };

  // ── Shared calendar body ──
  const calendarBody = (
    <>
      {/* Sticky weekday header */}
      <div style={{
        position: "sticky", top: 0, zIndex: 10,
        background: "#fff", borderBottom: "1px solid #e5e7eb", minWidth: 760,
      }}>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(7, minmax(106px, 1fr))", padding: "0 24px" }}>
          {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map((d, i) => (
            <div key={d} style={{
              padding: "8px 4px", textAlign: "center", fontSize: 11, fontWeight: 600,
              color: i === 0 || i === 6 ? "#9ca3af" : "#6b7280",
              textTransform: "uppercase", letterSpacing: "0.6px",
            }}>
              {d}
            </div>
          ))}
        </div>
      </div>

      {/* Top sentinel for infinite scroll */}
      <div ref={topSentinelRef} style={{ height: 1 }} />

      {/* Month sections */}
      <div style={{ padding: "0 24px 80px", minWidth: 760 }}>
        {months.map((month, mi) => (
          <MonthSection
            key={monthKey(month)}
            month={month}
            monthRef={mi === pastCount ? currentMonthRef : null}
            isCurrentMonth={mi === pastCount}
            {...cellProps}
          />
        ))}
        {isLoadingMore && (
          <div style={{ textAlign: "center", padding: "20px 0", fontSize: 12, color: "#9ca3af" }}>
            Loading…
          </div>
        )}
        <div style={{ marginTop: 6, paddingTop: 14, paddingBottom: 8, borderTop: "1px solid #f3f4f6" }}>
          <span style={{ fontSize: 11, color: "#9ca3af" }}>
            Click a day to create a session · Click a session card to view/edit
          </span>
          <span style={{ marginLeft: 12, display: "inline-flex", gap: 5, alignItems: "center" }}>
            <kbd style={CS.kbd}>⌘C</kbd>
            <span style={{ fontSize: 11, color: "#9ca3af" }}>copy</span>
            <kbd style={CS.kbd}>⌘V</kbd>
            <span style={{ fontSize: 11, color: "#9ca3af" }}>paste</span>
            <kbd style={CS.kbd}>Del</kbd>
            <span style={{ fontSize: 11, color: "#9ca3af" }}>delete</span>
          </span>
        </div>
      </div>

      {/* Bottom sentinel for infinite scroll */}
      <div ref={bottomSentinelRef} style={{ height: 1 }} />
    </>
  );

  // ── Render ──
  return (
    <div
      onWheel={!embedded ? handleOuterWheel : undefined}
      style={{
        height: embedded ? "auto" : "100%",
        width: "100%",
        background: "#fff",
      }}
    >
      <div style={{
        height: embedded ? "auto" : "100%",
        display: "flex", flexDirection: "column", background: "#fff",
        ...(embedded ? {} : { maxWidth: "var(--cal-page-max-width, 1440px)", width: "100%", margin: "0 auto" }),
      }}>

      {/* ── Compact header ── */}
      <div style={{
        flexShrink: 0, background: "#fff", borderBottom: "1px solid #e5e7eb",
        padding: "10px 24px", display: "flex", alignItems: "center", gap: 8,
      }}>
        {/* Left: selected student identity */}
        <div style={{ flex: 1, minWidth: 0 }}>
          {selectedStudent ? (
            <div style={{ display: "flex", alignItems: "baseline", gap: 6, minWidth: 0 }}>
              <span style={{
                fontSize: 15, fontWeight: 700, color: "#111827",
                letterSpacing: "-0.2px", overflow: "hidden",
                textOverflow: "ellipsis", whiteSpace: "nowrap",
              }}>
                {selectedStudent.name}
              </span>
              <span style={{ fontSize: 12, fontWeight: 400, color: "#9ca3af", flexShrink: 0 }}>
                Schedule
              </span>
            </div>
          ) : (
            <span style={{ fontSize: 14, fontWeight: 500, color: "#d1d5db" }}>
              Student Calendar
            </span>
          )}
        </div>

        {/* Right: selector + Today */}
        {!lockedStudent?.id && (
          <StudentSearchSelect
            students={students}
            value={selectedStudent?.id || ""}
            onChange={handleSelectStudent}
          />
        )}

        <button onClick={scrollToToday} style={CS.todayBtn}>Today</button>
      </div>

      {/* ── Calendar area ── */}
      {embedded ? (
        <div style={{ overflowX: "auto" }}>{calendarBody}</div>
      ) : (
        <div style={{ flex: 1, overflow: "hidden" }}>
          <div ref={containerRef} style={{ height: "100%", overflowY: "auto", overflowX: "auto", background: "#fff" }}>
            {calendarBody}
          </div>
        </div>
      )}

      {/* ── Session modal (centered, replaces floating panel) ── */}
      {activePanel && selectedStudent && (
        <SessionModal
          activePanel={activePanel}
          students={students}
          tutors={tutors}
          sessions={sessions}
          onSave={handleSaveSession}
          onCreate={handleCreateSession}
          onDelete={handleDeleteSession}
          onClose={handleCloseModal}
          isPastLocked={isPastLocked}
          forceReadOnly={readOnly}
        />
      )}

      {/* ── Paste-conflict create modal ── */}
      <StudentCreateSessionModal
        isOpen={!!createDate}
        onClose={() => { setCreateDate(null); setCreateInitialData(null); }}
        student={selectedStudent}
        date={createDate}
        tutors={tutors}
        onCreate={handleCreateSession}
        initialData={createInitialData}
      />

      <SessionNoteTooltip note={hoveredNote} anchorRect={tooltipAnchor} isVisible={!!hoveredNote} />
      </div>
    </div>
  );
}

// ─── Static styles ────────────────────────────────────────────────────────────

const CS = {
  todayBtn: {
    padding: "6px 14px",
    borderRadius: 8,
    border: "1px solid #e5e7eb",
    background: "#fff",
    fontSize: 13,
    fontWeight: 500,
    color: "#374151",
    cursor: "pointer",
    flexShrink: 0,
  },
  kbd: {
    background: "#f3f4f6",
    border: "1px solid #e5e7eb",
    borderRadius: 4,
    padding: "1px 6px",
    fontSize: 10,
    fontFamily: "monospace",
    color: "#6b7280",
  },
};
